import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import EditCourseCategory from './EditCourseCategory';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { CreateCourseCategoryInput, CourseCategory, UpdateCourseCategoryNameInput } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Training/Categories/PathItems';
import { CourseCategoryManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import AISuggestions from '../../components/AISuggestions';
import { useCourseCategories } from '../../providers/CourseCategoryProvider';




const CourseCategoryList: React.FC = () => {
  const { onError } = useToast();
  const { courseCategories, loadCourseCategories } = useCourseCategories();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string, name: string } | null>(null);
  const [editingCategory, setEditingCategory] = useState<CourseCategory | null>(null);

  const loadCategories = async () => {
    await loadCourseCategories()
  };

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleDialogOpen = () => {
    setNewCategoryName('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewCategoryName('');
  };

  const handleDialogOk = async () => {
    if (newCategoryName.trim()) {
      onError((await Session.getClient().request(CourseCategoryManagement.createCourseCategory({ name: newCategoryName.trim() } as CreateCourseCategoryInput)))
        .maybeOk(loadCategories));
    }
    handleDialogClose();
  };


  const handleDeleteClick = (id: string, name: string) => {
    setCategoryToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleDeleteDialogOk = async () => {
    if (categoryToDelete) {
      onError((await Session.getClient().addPathParam('courseCategoryId', categoryToDelete.id).request(CourseCategoryManagement.deleteCourseCategory()))
        .maybeOk(loadCategories));
    }
    handleDeleteDialogClose();
  };


  const handleEdit = (cat: CourseCategory) => {
    setEditingCategory(cat);
  };


  const handleEditSave = async (name: string, translationKey: string) => {
    if (editingCategory) {
      const client = Session.getClient().addPathParam('courseCategoryId', editingCategory.id);
      await onError((await client.request(CourseCategoryManagement.updateCourseCategoryName({ name } as UpdateCourseCategoryNameInput))).maybeOk(loadCategories));
      setEditingCategory(null);
    }
  };


  // No published toggle in backend model, so skip this for now


  if (editingCategory) {
    return (
      <EditCourseCategory
        name={editingCategory.name}
        translationKey={editingCategory.translationKey}
        status={editingCategory.state}
        onBack={() => setEditingCategory(null)}
        onSave={handleEditSave}
        onTransition={async (action: string) => {
          const client = Session.getClient().addPathParam('courseCategoryId', editingCategory.id);
          return (await client.request(CourseCategoryManagement.updateCourseCategoryState({ stateTransition: action })))
            .maybeOk(() => {
              loadCategories()
              setEditingCategory(null);
            });
        }}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h6" gutterBottom>Course Categories</Typography>
        <AISuggestions 
          client={Session.getClient()}
          currentSnapshot={JSON.stringify(courseCategories)}
          usageInstructions={CourseCategoryManagement.toAgentInstructions()}
          developerMessage={'The user is in the view where they can see a list of course categories. For any of the operations ' +
            'regarding a specific category the path parameter with the name courseCategoryId MUST be specified with that category id.'}
          onOperationsApplied={loadCategories}
        />
        <Button variant="contained" color="primary" onClick={handleDialogOpen}>New Category</Button>
      </Box>
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Category Name"
            fullWidth
            autoFocus
            value={newCategoryName}
            onChange={e => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this category?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteDialogOk} variant="contained" color="error">OK</Button>
        </DialogActions>
      </Dialog>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight="bold">Category Name</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Published</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {courseCategories.map(cat => (
              <TableRow key={cat.id}>
                <TableCell>
                  <Typography>{cat.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{cat.translationKey}</Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton edge="end" onClick={() => handleEdit(cat)}><EditIcon /></IconButton>
                  <IconButton edge="end" color="error" onClick={() => handleDeleteClick(cat.id, cat.name)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CourseCategoryList;
